import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { 
  Users, 
  MessageCircle, 
  Sparkles,
  MapPin,
  Briefcase,
  Flag,
  Loader2,
  Search
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { FollowButton } from '../profile/FollowButton';
import { UserReportDialog } from '../admin/UserReportDialog';
import { MessagesPopup } from '../messaging/MessagesPopup';

interface User {
  id: string;
  full_name: string;
  username: string;
  avatar_url: string;
  bio?: string;
  location?: string;
  followers_count: number;
  following_count: number;
  is_following?: boolean;
  profession?: string;
}

export function DiscoverPage() {
  const navigate = useNavigate();
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<'all' | 'nearby' | 'suggested'>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [showReportDialog, setShowReportDialog] = useState<string | null>(null);
  const [showMessages, setShowMessages] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);

  useEffect(() => {
    loadUsers();
  }, [filter]);

  const loadUsers = async () => {
    try {
      let query = supabase
        .from('profiles')
        .select(`
          *,
          followers:follows!follows_following_id_fkey(count),
          following:follows!follows_follower_id_fkey(count),
          is_following:follows!follows_following_id_fkey(follower_id)
        `);

      // Apply filters
      switch (filter) {
        case 'nearby':
          query = query.not('location', 'is', null);
          break;
        case 'suggested':
          query = query.order('followers_count', { ascending: false });
          break;
      }

      const { data, error } = await query;

      if (error) throw error;
      
      setUsers(data?.map(user => ({
        ...user,
        followers_count: user.followers?.[0]?.count || 0,
        following_count: user.following?.[0]?.count || 0,
        is_following: user.is_following?.length > 0
      })) || []);
    } catch (error) {
      console.error('Error loading users:', error);
      setError('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const handleFollowChange = (userId: string, isFollowing: boolean) => {
    setUsers(prev => prev.map(user => {
      if (user.id === userId) {
        return {
          ...user,
          is_following: isFollowing,
          followers_count: isFollowing 
            ? (user.followers_count || 0) + 1 
            : Math.max(0, (user.followers_count || 0) - 1)
        };
      }
      return user;
    }));
  };

  const handleMessage = (e: React.MouseEvent, userId: string) => {
    e.preventDefault();
    e.stopPropagation();
    setSelectedUserId(userId);
    setShowMessages(true);
  };

  const filteredUsers = users.filter(user => {
    if (!searchQuery) return true;
    
    const search = searchQuery.toLowerCase();
    return (
      user.full_name.toLowerCase().includes(search) ||
      user.username.toLowerCase().includes(search) ||
      user.bio?.toLowerCase().includes(search) ||
      user.location?.toLowerCase().includes(search) ||
      user.profession?.toLowerCase().includes(search)
    );
  });

  return (
    <div className="max-w-7xl mx-auto space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Sparkles className="w-8 h-8 text-royal-purple" />
          <h1 className="text-2xl font-bold">Discover People</h1>
        </div>
      </div>

      {/* Search and filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search by name, location, profession..."
            className="w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-royal-purple focus:border-transparent"
          />
        </div>

        <div className="flex gap-2">
          {[
            { id: 'all', label: 'All', icon: Users },
            { id: 'nearby', label: 'Nearby', icon: MapPin },
            { id: 'suggested', label: 'Suggested', icon: Sparkles }
          ].map(({ id, label, icon: Icon }) => (
            <button
              key={id}
              onClick={() => setFilter(id as typeof filter)}
              className={cn(
                "flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors",
                filter === id
                  ? "bg-royal-purple text-white"
                  : "bg-gray-100 text-gray-700 hover:bg-gray-200"
              )}
            >
              <Icon className="w-5 h-5" />
              <span className="hidden sm:inline">{label}</span>
            </button>
          ))}
        </div>
      </div>

      {error && (
        <div className="p-4 rounded-lg bg-red-50 text-red-600">
          {error}
        </div>
      )}

      {/* User grid */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 text-royal-purple animate-spin" />
        </div>
      ) : filteredUsers.length > 0 ? (
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {filteredUsers.map((user) => (
            <div
              key={user.id}
              className="bg-white rounded-xl shadow-sm hover:shadow-md transition-all duration-200 overflow-hidden group"
            >
              <div className="relative h-32 bg-gradient-to-br from-royal-purple/30 to-royal-purple/10">
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/30 to-transparent" />
                
                <div className="relative p-6">
                  <div className="flex items-start gap-4">
                    <img
                      src={user.avatar_url || `https://api.dicebear.com/7.x/initials/svg?seed=${user.full_name}`}
                      alt={user.full_name}
                      className="w-16 h-16 rounded-full ring-4 ring-white shadow-lg"
                    />
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold text-lg text-white truncate">
                        {user.full_name}
                      </h3>
                      <p className="text-sm text-gray-200 truncate">
                        @{user.username}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="p-6 pt-4">
                {user.bio && (
                  <p className="text-sm text-gray-600 line-clamp-2">
                    {user.bio}
                  </p>
                )}

                <div className="mt-4 flex flex-wrap gap-2">
                  {user.location && (
                    <div className="flex items-center gap-1 text-sm text-gray-500">
                      <MapPin className="w-4 h-4" />
                      <span>{user.location}</span>
                    </div>
                  )}
                  {user.profession && (
                    <div className="flex items-center gap-1 text-sm text-gray-500">
                      <Briefcase className="w-4 h-4" />
                      <span>{user.profession}</span>
                    </div>
                  )}
                </div>

                <div className="mt-4 flex items-center justify-between text-sm text-gray-500">
                  <div className="space-x-4">
                    <span>{user.followers_count || 0} followers</span>
                    <span>{user.following_count || 0} following</span>
                  </div>
                </div>

                <div className="mt-4 flex items-center gap-2">
                  <FollowButton
                    userId={user.id}
                    isFollowing={user.is_following || false}
                    onFollowChange={(isFollowing) => handleFollowChange(user.id, isFollowing)}
                    className="flex-1"
                  />
                  <button
                    onClick={(e) => handleMessage(e, user.id)}
                    className="p-2 text-gray-600 hover:text-royal-purple hover:bg-royal-purple/10 rounded-lg transition-colors"
                  >
                    <MessageCircle className="w-5 h-5" />
                  </button>
                  <button
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setShowReportDialog(user.id);
                    }}
                    className="p-2 text-gray-600 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                  >
                    <Flag className="w-5 h-5" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center py-12">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gray-100 mb-4">
            <Users className="w-8 h-8 text-gray-400" />
          </div>
          <p className="text-gray-500">
            {searchQuery
              ? 'No users found matching your search'
              : 'No users to display'}
          </p>
        </div>
      )}

      {/* Report dialog */}
      {showReportDialog && (
        <UserReportDialog
          userId={showReportDialog}
          onClose={() => setShowReportDialog(null)}
        />
      )}

      {/* Messages popup */}
      {showMessages && selectedUserId && (
        <MessagesPopup
          onClose={() => {
            setShowMessages(false);
            setSelectedUserId(null);
          }}
          initialConversationId={null}
          initialUserId={selectedUserId}
        />
      )}
    </div>
  );
}